/**
 * Tenancy and fleet domain types shared by the database layer, API and web console.
 * Mirrors supabase/migrations/*_tenancy.sql.
 */

/** Tenant roles, lowest to highest privilege. */
export const TENANT_ROLES = ["viewer", "safety", "fleet_manager", "admin"] as const;
export type TenantRole = (typeof TENANT_ROLES)[number];

export function roleAtLeast(role: TenantRole, min: TenantRole): boolean {
  return TENANT_ROLES.indexOf(role) >= TENANT_ROLES.indexOf(min);
}

export const SITE_KINDS = ["site", "warehouse", "department", "zone"] as const;
export type SiteKind = (typeof SITE_KINDS)[number];

export const VEHICLE_TYPES = ["forklift", "reach_truck", "order_picker", "pallet_truck", "other"] as const;
export type VehicleType = (typeof VEHICLE_TYPES)[number];

export const POWER_TYPES = ["electric", "diesel", "lpg"] as const;
export type PowerType = (typeof POWER_TYPES)[number];

export type Tenant = { id: string; name: string; slug: string; createdAt: string };

export type Site = {
  id: string;
  tenantId: string;
  parentId: string | null;
  name: string;
  kind: SiteKind;
  address: string | null;
  lat: number | null;
  lon: number | null;
};

export type Vehicle = {
  id: string;
  tenantId: string;
  siteId: string;
  fleetNo: string;
  type: VehicleType;
  power: PowerType;
  make: string | null;
  model: string | null;
  serialNo: string | null;
  batteryVoltage: number | null;
  active: boolean;
  /** Raise "start without card" alarms (off for vehicles without a card reader). */
  unauthorizedStartAlarm: boolean;
  deviceId: string | null;
};

export type Membership = { tenantId: string; userId: string; role: TenantRole; siteIds: string[] };

export type CurrentUser = {
  id: string;
  email: string;
  displayName: string;
  platformAdmin: boolean;
  memberships: Membership[];
};

/** Build the Tenant -> Site (nested) tree used by the console rail. */
export type SiteNode = Site & { children: SiteNode[]; vehicles: Vehicle[] };

export function buildSiteTree(sites: Site[], vehicles: Vehicle[]): SiteNode[] {
  const nodes = new Map<string, SiteNode>(sites.map((s) => [s.id, { ...s, children: [], vehicles: [] }]));
  for (const v of vehicles) nodes.get(v.siteId)?.vehicles.push(v);
  const roots: SiteNode[] = [];
  for (const n of nodes.values()) {
    const parent = n.parentId ? nodes.get(n.parentId) : undefined;
    if (parent) parent.children.push(n);
    else roots.push(n);
  }
  const sort = (xs: SiteNode[]) => {
    xs.sort((a, b) => a.name.localeCompare(b.name, "tr"));
    for (const x of xs) {
      x.vehicles.sort((a, b) => a.fleetNo.localeCompare(b.fleetNo, "tr", { numeric: true }));
      sort(x.children);
    }
  };
  sort(roots);
  return roots;
}

/* ---------------- Sprint 2: devices and invitations ---------------- */

/** Device ids as printed on the label, e.g. FTS-000123 (same rule as the ICD topic id). */
export const DEVICE_ID_PATTERN = /^[A-Z0-9][A-Z0-9-]{2,31}$/;

export type Device = {
  deviceId: string;
  /** null = in Expartise inventory, not yet claimed by a customer. */
  tenantId: string | null;
  vehicleId: string | null;
  hw: string | null;
  fw: string | null;
  imei: string | null;
  iccid: string | null;
  online: boolean;
  lastSeenAt: string | null;
  claimedAt: string | null;
  revoked: boolean;
  /**
   * The device has a card reader (operator login, card list, unauthorized-start detection). False for
   * units bridged over JT/T 808 (ADR 0021, ADR 0023). Derived by the database from the hello `hw`.
   */
  hasCardReader: boolean;
};

/**
 * Same rule as public.fts_hw_has_card_reader (ADR 0023): a device reporting hw "jt808" or "jt808:<model>"
 * (the JT/T 808 bridge, jt808/map.ts) has no card reader; before its first hello (hw unknown, empty or blank) the bridge's
 * default id "MDVR-<number>" is the fallback. Every other device has one.
 */
export function deviceHasCardReader(deviceId: string | null, hw: string | null | undefined): boolean {
  const h = hw?.trim();
  if (h) return !(h === "jt808" || h.startsWith("jt808:"));
  return !(deviceId ?? "").startsWith("MDVR-");
}

/** Returned once at registration; the secret is flashed into the device, the claim code goes on its label. */
export type RegisteredDevice = { deviceId: string; secret: string; claimCode: string };

export const INVITATION_STATUSES = ["pending", "accepted", "revoked", "expired"] as const;
export type InvitationStatus = (typeof INVITATION_STATUSES)[number];

export type Invitation = {
  id: string;
  tenantId: string;
  email: string;
  role: TenantRole;
  /** Empty = whole tenant. */
  siteIds: string[];
  createdAt: string;
  expiresAt: string;
  status: InvitationStatus;
};

/** What an invitee sees before signing in (no e-mail address, no ids). */
export type InvitationPreview = { tenantName: string; role: TenantRole; status: InvitationStatus; expiresAt: string };

/**
 * Error tokens raised by the database functions as `fts:<token>` (see the devices/invitations migration).
 * Clients map them to translated messages.
 */
export const DB_ERRORS = [
  "forbidden",
  "invalid_device_id",
  "device_exists",
  "device_not_found",
  "device_revoked",
  "device_busy",
  "vehicle_busy",
  "vehicle_inactive",
  "tenant_mismatch",
  "claim_invalid",
  "invalid_email",
  "invalid_scope",
  "already_member",
  "invitation_invalid",
  "wrong_email",
  "scope_changed",
  "site_is_last_scope",
  "rate_limited",
  // Sprint 7b (ADR 0018): report date range (max 92 days; route max 24 h) or KPI period not allowed.
  "invalid_report_range",
  "report_too_large",
  "export_too_large",
  "vehicle_has_history",
  "card_in_use",
  "invalid_card",
  "invalid_permission",
  "operator_left",
  "invalid_left_on",
  // Sprint 6a (ADR 0011): alarms and notification settings.
  "invalid_contact",
  "invalid_alert_settings",
  "already_acknowledged",
  "note_too_long",
  // ADR 0014: live watch.
  "device_offline",
  "too_many_live_watches",
  // Sprint 7b (ADR 0019): web push.
  "invalid_push_subscription",
  "push_device_limit",
  // Sprint 6b (ADR 0015): geofences.
  "invalid_geofence",
  "too_many_geofences",
  // Sprint 7a (ADR 0016): pre-shift checklist.
  "checklist_token_invalid",
  "invalid_checklist",
  "invalid_photo",
  "invalid_template",
  "invalid_shift",
  "too_many_shifts",
  "invalid_recipient",
  "fault_resolved",
  "form_changed",
  // Sprint 7a (ADR 0017): a module setting already sent to the device cannot be cleared.
  "module_setting_locked",
] as const;
export type DbError = (typeof DB_ERRORS)[number];

export function parseDbError(message: string | undefined): DbError | null {
  const m = /fts:([a-z_]+)/.exec(message ?? "");
  return m && (DB_ERRORS as readonly string[]).includes(m[1]!) ? (m[1] as DbError) : null;
}
